import { zodResolver } from '@hookform/resolvers/zod';
import {
  type ExchangeRateEntry,
  parseRateInput,
  rateLabelSchema,
  UI_CURRENCIES,
} from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { FormError } from '../components/FormError';
import { PageHeader } from '../components/PageHeader';
import { StaleRateNotice } from '../components/StaleRateNotice';
import { Button } from '../components/ui/button';
import { Card, CardTitle } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { api } from '../lib/api';
import { useMe } from '../lib/auth';
import { useCurrentRates, useRateHistory } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { useIdempotencyKey } from '../lib/members';
import { todayIn } from '../lib/dates';
import { formatRateValue } from '../lib/money';

function RateLine({ entry }: { entry: ExchangeRateEntry }) {
  const { t, i18n } = useTranslation();
  return (
    <span dir="ltr" className="tabular-nums">
      {t('rates.equation', {
        base: entry.baseCurrency,
        rate: formatRateValue(entry.rate, i18n.language),
        quote: entry.quoteCurrency,
      })}
    </span>
  );
}

function useDateFormat(timeZone: string | undefined) {
  const { i18n } = useTranslation();
  return new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeZone });
}

const addRateSchema = z.object({
  quoteCurrency: z.string(),
  rate: z
    .string()
    .transform((raw) => parseRateInput(raw))
    .refine((v) => v !== null, { message: 'rate' }),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  label: rateLabelSchema,
});

function AddRateForm({
  baseCurrency,
  quotes,
  timeZone,
}: {
  baseCurrency: string;
  quotes: string[];
  timeZone: string;
}) {
  const { t, i18n } = useTranslation();
  const invalidate = useInvalidateLedger();
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const today = todayIn(timeZone);
  const form = useForm<z.input<typeof addRateSchema>, unknown, z.output<typeof addRateSchema>>({
    resolver: zodResolver(addRateSchema),
    defaultValues: { quoteCurrency: quotes[0] ?? '', rate: '', date: today, label: '' },
  });
  const quote = useWatch({ control: form.control, name: 'quoteCurrency' });
  const rawRate = useWatch({ control: form.control, name: 'rate' });
  const preview = parseRateInput(rawRate);
  const add = useMutation({
    mutationFn: (v: z.output<typeof addRateSchema>) =>
      api.rates.create(
        {
          quoteCurrency: v.quoteCurrency,
          rate: v.rate!,
          // Today: from now on. An earlier day: noon UTC that day, which is that same day in the
          // family's time zone (Beirut is UTC+2 or +3), so it applies to that whole day.
          effectiveAt: v.date === today ? undefined : `${v.date}T12:00:00Z`,
          label: v.label,
        },
        { idempotencyKey },
      ),
    onSuccess: async () => {
      renewKey();
      form.reset({ quoteCurrency: quote, rate: '', date: todayIn(timeZone), label: '' });
      await invalidate();
    },
  });
  const errors = form.formState.errors;
  return (
    <Card>
      <form
        noValidate
        className="flex flex-col gap-4"
        onSubmit={form.handleSubmit((v) => add.mutate(v))}
      >
        <CardTitle>{t('rates.addTitle')}</CardTitle>
        <FormError error={add.error} />
        {quotes.length > 1 && (
          <Field label={t('rates.currency')}>
            <Select {...form.register('quoteCurrency')}>
              {quotes.map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <div className="flex flex-col gap-1.5">
          <Field
            label={t('rates.rateLabel', { base: baseCurrency, quote })}
            hint={t('rates.rateHint', { base: baseCurrency, quote })}
          >
            <Input
              inputMode="decimal"
              autoComplete="off"
              dir="ltr"
              placeholder="89,500"
              {...form.register('rate')}
            />
          </Field>
          {preview && (
            <p className="text-sm text-muted" aria-live="polite">
              <span dir="ltr" className="tabular-nums">
                {t('rates.equation', {
                  base: baseCurrency,
                  rate: formatRateValue(preview, i18n.language),
                  quote,
                })}
              </span>
            </p>
          )}
          {errors.rate && <p className="text-sm text-danger">{t('rates.invalidRate')}</p>}
        </div>
        <Field label={t('rates.date')} hint={t('rates.dateHint')} error={errors.date}>
          <Input type="date" max={today} {...form.register('date')} />
        </Field>
        <Field label={t('rates.label')} hint={t('rates.labelHint')} error={errors.label}>
          <Input autoComplete="off" {...form.register('label')} />
        </Field>
        <Button type="submit" disabled={add.isPending}>
          {t('rates.save')}
        </Button>
      </form>
    </Card>
  );
}

function History({ timeZone }: { timeZone: string }) {
  const { t } = useTranslation();
  const history = useRateHistory();
  const date = useDateFormat(timeZone);
  const items = history.data?.pages.flatMap((p) => p.items) ?? [];
  return (
    <Card className="flex flex-col gap-2">
      <CardTitle>{t('rates.history')}</CardTitle>
      <FormError error={history.error} />
      {history.isSuccess && items.length === 0 && (
        <p className="text-sm text-muted">{t('rates.historyEmpty')}</p>
      )}
      <ul className="flex flex-col divide-y divide-line">
        {items.map((entry) => (
          <li key={entry.id} className="flex flex-col py-2">
            <span className="font-medium">
              <RateLine entry={entry} />
            </span>
            <span className="text-sm text-muted">
              {[
                date.format(new Date(entry.effectiveAt)),
                ...(entry.label ? [entry.label] : []),
                ...(entry.createdByName ? [t('rates.by', { name: entry.createdByName })] : []),
              ].join(' · ')}
            </span>
          </li>
        ))}
      </ul>
      {history.hasNextPage && (
        <Button
          variant="ghost"
          onClick={() => void history.fetchNextPage()}
          disabled={history.isFetchingNextPage}
        >
          {t('rates.loadMore')}
        </Button>
      )}
    </Card>
  );
}

/** Family → Exchange rates: today's rate, its history, and (for owner and parents) a new rate. */
export function RatesPage() {
  const { t } = useTranslation();
  const me = useMe();
  const rates = useCurrentRates();
  const timeZone = me.data?.family.timezone;
  const date = useDateFormat(timeZone);
  if (!me.data || rates.isPending) return <p role="status">{t('app.loading')}</p>;
  if (rates.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={t('rates.title')} back="/family" />
        <FormError error={rates.error} />
      </div>
    );
  }
  const items = rates.data.items.filter((i) =>
    (UI_CURRENCIES as readonly string[]).includes(i.quoteCurrency),
  );
  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('rates.title')} back="/family" />
      <p className="text-sm text-muted">{t('rates.intro')}</p>
      <StaleRateNotice showLink={false} />
      {items.map((i) => (
        <Card key={i.quoteCurrency} className="flex flex-col gap-1">
          <span className="text-sm text-muted">
            {t('rates.current', { quote: i.quoteCurrency })}
          </span>
          {i.current ? (
            <>
              <span className="text-2xl font-semibold">
                <RateLine entry={i.current} />
              </span>
              <span className="text-sm text-muted">
                {[
                  t('rates.since', { date: date.format(new Date(i.current.effectiveAt)) }),
                  ...(i.current.label ? [i.current.label] : []),
                ].join(' · ')}
              </span>
            </>
          ) : (
            <span className="text-lg font-medium">{t('rates.none')}</span>
          )}
        </Card>
      ))}
      {rates.data.canManage && items.length > 0 && timeZone && (
        <AddRateForm
          baseCurrency={rates.data.baseCurrency}
          quotes={items.map((i) => i.quoteCurrency)}
          timeZone={timeZone}
        />
      )}
      {!rates.data.canManage && <p className="text-sm text-muted">{t('rates.cannotManage')}</p>}
      {timeZone && <History timeZone={timeZone} />}
    </div>
  );
}
